import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, Trash2, ShoppingCart, Store } from "lucide-react";
import { toast } from "sonner";
import { PublicLayout } from "../components/PublicLayout";
import { ProductImage } from "../components/shop/ProductCard";
import { CheckoutForm } from "../components/shop/CheckoutForm";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { api } from "../lib/api";
import { nok, shopUrl } from "../lib/shop";
import { clearCart, reconcileCart, removeFromCart, setCartQuantity, useCart } from "../lib/cart";
import { useLang } from "../context/LanguageContext";

// Kasse: /butikk/:organizerId/kasse – korglinjer (tal/fjern) + CheckoutForm. Éi korg per arrangør; prisar/lager vert sjekka mot ferske data ved lasting.
export default function ShopCheckoutPage() {
  const { organizerId } = useParams();
  const { t } = useLang();
  const { cart } = useCart();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [removedNames, setRemovedNames] = useState([]);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    window.scrollTo({ top: 0 });
    api
      .get(`/shop/organizers/${organizerId}`)
      .then((res) => {
        setShop(res.data);
        const { removed } = reconcileCart(res.data.products || []);
        if (removed.length) setRemovedNames(removed);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [organizerId]);

  const productById = useMemo(() => new Map((shop?.products || []).map((p) => [p.id, p])), [shop]);
  const items = cart.organizer_id === organizerId ? cart.items : [];
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  const changeQty = (item, next) => {
    const p = productById.get(item.product_id);
    const max = Math.max(1, Math.min(20, p?.max_per_order || 20));
    const q = Math.min(max, Math.max(1, Number.isFinite(next) ? next : 1));
    if (next > max) toast.info(t("Maks {n} av denne varen", { n: max }));
    setCartQuantity(item.product_id, q);
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid gap-6 lg:grid-cols-12" data-testid="checkout-loading">
          <div className="lg:col-span-7 space-y-3"><Skeleton className="h-8 w-1/2" /><Skeleton className="h-24 rounded-xl" /><Skeleton className="h-24 rounded-xl" /></div>
          <div className="lg:col-span-5"><Skeleton className="h-[480px] rounded-2xl" /></div>
        </div>
      </PublicLayout>
    );
  }

  if (notFound || !shop) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center space-y-4" data-testid="checkout-not-found">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-secondary text-muted-foreground"><Store className="w-7 h-7" /></span>
          <h1 className="text-2xl font-semibold">{t("Fant ikke butikken")}</h1>
          <Button asChild data-testid="checkout-not-found-link"><Link to="/butikk"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Til alle butikker")}</Link></Button>
        </div>
      </PublicLayout>
    );
  }

  const org = shop.organizer;

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 space-y-6" data-testid="checkout-page">
        <Link to={shopUrl(org.id)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150" data-testid="checkout-back-link">
          <ArrowLeft className="w-4 h-4" />
          {t("Fortsett å handle hos {org}", { org: org.org_name })}
        </Link>
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="checkout-title">{t("Kassen")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("Selger: {org}", { org: org.org_name })}</p>
        </div>

        {removedNames.length > 0 && (
          <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-4 text-sm" data-testid="checkout-removed-note">
            {t("Noen varer er ikke tilgjengelige lenger og ble fjernet fra handlekurven: {names}", { names: removedNames.join(", ") })}
          </div>
        )}

        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center space-y-3" data-testid="checkout-empty">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]"><ShoppingCart className="w-7 h-7" /></span>
            <h2 className="font-heading font-semibold text-lg">{t("Handlekurven er tom")}</h2>
            <p className="text-sm text-muted-foreground">{t("Legg til varer fra butikken, så finner du dem igjen her.")}</p>
            <Button asChild variant="outline" data-testid="checkout-empty-shop-link"><Link to={shopUrl(org.id)}><Store className="w-4 h-4 mr-1.5" /> {t("Til butikken")}</Link></Button>
          </div>
        ) : (
          <div className="grid gap-6 lg:gap-8 lg:grid-cols-12 items-start">
            {/* Korglinjer */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="lg:col-span-7 space-y-4">
              <Card className="rounded-2xl border-border">
                <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
                  <CardTitle className="text-lg font-heading">{items.length === 1 ? t("{n} vare", { n: 1 }) : t("{n} varer", { n: items.length })}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => clearCart()} className="text-muted-foreground" data-testid="cart-clear-button"><Trash2 className="w-4 h-4 mr-1.5" /> {t("Tøm handlekurven")}</Button>
                </CardHeader>
                <CardContent className="divide-y divide-border p-0">
                  {items.map((it) => {
                    const p = productById.get(it.product_id);
                    const max = Math.max(1, Math.min(20, p?.max_per_order || 20));
                    return (
                      <div key={it.product_id} className="flex gap-4 p-4" data-testid={`cart-line-${it.product_id}`}>
                        <Link to={`/butikk/${org.id}/vare/${it.product_id}`} className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted border border-border shrink-0">
                          <ProductImage product={{ ...it, id: it.product_id }} sizes="80px" />
                        </Link>
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <Link to={`/butikk/${org.id}/vare/${it.product_id}`} className="font-medium leading-snug hover:underline underline-offset-4 line-clamp-2" data-testid={`cart-line-name-${it.product_id}`}>{it.name}</Link>
                              <p className="text-sm text-muted-foreground tabular-nums">{nok(it.price)} {t("per stk.")}</p>
                              {p?.stock_status === "low" && <p className="text-xs text-[hsl(var(--warning))]" data-testid={`cart-line-low-${it.product_id}`}>{t("Bare {n} igjen", { n: p.remaining })}</p>}
                            </div>
                            <p className="font-semibold tabular-nums shrink-0" data-testid={`cart-line-total-${it.product_id}`}>{nok(it.price * it.quantity)}</p>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <div className="inline-flex items-center rounded-lg border border-border">
                              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-r-none" onClick={() => changeQty(it, it.quantity - 1)} disabled={it.quantity <= 1} aria-label={t("Færre")} data-testid={`cart-qty-decrease-${it.product_id}`}><Minus className="w-4 h-4" /></Button>
                              <Input type="number" min={1} max={max} inputMode="numeric" value={it.quantity} onChange={(e) => changeQty(it, parseInt(e.target.value, 10))} className="h-9 w-14 text-center border-0 border-x border-border rounded-none tabular-nums focus-visible:ring-0" aria-label={t("Antall")} data-testid={`cart-qty-${it.product_id}`} />
                              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-l-none" onClick={() => changeQty(it, it.quantity + 1)} disabled={it.quantity >= max} aria-label={t("Flere")} data-testid={`cart-qty-increase-${it.product_id}`}><Plus className="w-4 h-4" /></Button>
                            </div>
                            <Button type="button" variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => removeFromCart(it.product_id)} data-testid={`cart-remove-${it.product_id}`}><Trash2 className="w-4 h-4 mr-1.5" /> {t("Fjern")}</Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex justify-between gap-3 p-4 text-sm"><span className="text-muted-foreground">{t("Varesum")}</span><span className="font-medium tabular-nums" data-testid="cart-subtotal">{nok(subtotal)}</span></div>
                </CardContent>
              </Card>
              <p className="text-xs text-muted-foreground">{t("Frakt (ved sending) og totalsum ser du i oppsummeringen før du bestiller.")}</p>
            </motion.div>

            {/* Kasse */}
            <div className="lg:col-span-5">
              <Card className="rounded-2xl border-border shadow-lg lg:sticky lg:top-24">
                <CardHeader className="pb-3"><CardTitle className="text-lg font-heading">{t("Levering og betaling")}</CardTitle></CardHeader>
                <CardContent>
                  <CheckoutForm items={items} shop={shop} onSubmitted={() => clearCart()} />
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
